// AnnualReportInline — page-level section component for the donor
// detail's annual-report opt-in indicator.
//
// CHG-1665 / ITER-3774 (Wave 1 / Tier 1 — DonorDetail port).
//
// Consumed via the override-block:section-annual-report-inline in
// generated/screens/donor-detail.ts. The override-block's `render:`
// is replaced with `createElement(AnnualReportInline, { donor: r })`.
//
// Mutation (toggle) deferred: the v1 page wires PATCH via
// /donors/{id}/preferences. For the Tier-1 minimum this card is
// read-only — the actual toggle ships in a follow-up CHG where the
// mutation hook + opt-in copy land together.

import type { Donor } from "../../api/types";

export function AnnualReportInline({ donor }: { donor: Donor }) {
  // annual_report_opt_in is optional on the v2 type — surface a
  // tristate (Yes / No / Unknown) so the card reads sensibly when
  // the field hasn't been hydrated yet (backend DonorRead carries
  // it; older DonorRead projections may not).
  const state =
    donor.annual_report_opt_in == null
      ? "unknown"
      : donor.annual_report_opt_in
      ? "yes"
      : "no";
  const label =
    state === "yes"
      ? "Opted in"
      : state === "no"
      ? "Opted out"
      : "Unknown";
  const tone =
    state === "yes"
      ? { bg: "var(--color-success-tint)", fg: "var(--color-success)" }
      : state === "no"
      ? { bg: "var(--color-warning-tint)", fg: "var(--color-warning)" }
      : { bg: "var(--color-bg-subtle)", fg: "var(--color-text-secondary)" };

  return (
    <section
      data-testid="annual-report-inline"
      style={{
        padding: 16,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-md, 8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--color-text-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          Annual report
        </span>
        <span style={{ fontSize: 13, color: "var(--color-text)" }}>
          Send this donor the yearly impact report.
        </span>
      </div>
      <span
        data-testid="annual-report-state"
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 10px",
          fontSize: 12,
          fontWeight: 600,
          borderRadius: 999,
          background: tone.bg,
          color: tone.fg,
        }}
      >
        {label}
      </span>
    </section>
  );
}
